import { useEffect, useRef, useState } from 'react';
import { Input } from '../../../components/ui';
import { EditLineIcon, TrashIcon } from '../../../assets/icons';
import EcHierarchyTree from './EcHierarchyTree';
import AddLevelModal, { type NewLevel } from './AddLevelModal';
import type { HierarchyView } from './DefineBoundaryStep';

interface Level {
  id: string;
  name: string;
}

const INITIAL_LEVELS: Level[] = [
  { id: 'l1', name: 'Alwar BJP' },
  { id: 'l2', name: 'Mandal' },
  { id: 'l3', name: 'Shakti Kendra' },
  { id: 'l4', name: 'Booths' },
];

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col rounded-xl border border-line-soft bg-white p-6 shadow-card">
      {children}
    </div>
  );
}

/** Read-only rendering of the selected-hierarchy path reported by step 2. */
function SelectedHierarchy({ nodes }: { nodes: HierarchyView[] }) {
  const renderNode = (node: HierarchyView) => (
    <div key={node.id}>
      <div
        className={
          node.selected
            ? 'flex items-center gap-2.5 rounded-lg border border-[#bfdbfe] bg-[#eff6ff] px-3 py-2'
            : 'flex items-center gap-2.5 px-3 py-2'
        }
      >
        {node.icon ? (
          <node.icon className="size-5 shrink-0 text-brand" />
        ) : (
          <span className="flex size-5 shrink-0 items-center justify-center">
            <span className="size-1.5 rounded-full bg-[#94a3b8]" />
          </span>
        )}
        <span
          className={[
            'text-sm',
            node.selected ? 'font-semibold text-brand' : 'font-medium text-ink',
          ].join(' ')}
        >
          {node.label}
        </span>
        {node.level && (
          <span className="ml-auto rounded bg-[#f1f5f9] px-2 py-1 text-[10px] font-bold tracking-[0.25px] text-[#64748b]">
            {node.level}
          </span>
        )}
        {node.selected && !node.level && (
          <span className="ml-auto text-[10px] font-bold tracking-[0.5px] text-brand">SELECTED</span>
        )}
      </div>

      {node.sub && <div className="pl-[42px] text-xs text-[#94a3b8]">{node.sub}</div>}

      {node.children.length > 0 && (
        <div className="ml-[19px] flex flex-col gap-1 border-l border-dashed border-[#cbd5e1] pl-3">
          {node.children.map(renderNode)}
        </div>
      )}
    </div>
  );

  return <div className="flex flex-col gap-1">{nodes.map(renderNode)}</div>;
}

/** Step 3 — sequence the organization's administrative levels against the EC hierarchy. */
export default function BuildHierarchyStep({
  selection,
  onLevelsChange,
}: {
  selection?: HierarchyView[];
  onLevelsChange?: (levels: string[]) => void;
}) {
  const [levels, setLevels] = useState<Level[]>(INITIAL_LEVELS);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const nextId = useRef(INITIAL_LEVELS.length + 1);

  // Report the ordered level names up so step 4's template can be prefilled with them.
  useEffect(() => {
    onLevelsChange?.(levels.map((l) => l.name));
  }, [levels, onLevelsChange]);

  const createLevel = (level: NewLevel) => {
    const id = `l${nextId.current++}`;
    setLevels((ls) => [...ls, { id, name: level.name }]);
    setAddOpen(false);
  };

  const startEdit = (level: Level) => {
    setEditingId(level.id);
    setDraft(level.name);
  };

  const saveEdit = () => {
    if (!editingId) return;
    const name = draft.trim();
    setLevels((ls) => ls.map((l) => (l.id === editingId ? { ...l, name: name || l.name } : l)));
    setEditingId(null);
  };

  const deleteLevel = (id: string) => {
    setLevels((ls) => ls.filter((l) => l.id !== id));
    if (editingId === id) setEditingId(null);
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      {/* Left — selection from step 2 (falls back to the EC reference tree) */}
      <Card>
        {selection && selection.length > 0 ? (
          <>
            <h2 className="text-lg font-bold leading-6 text-ink">Selected Constituency Hierarchy</h2>
            <div className="mt-6">
              <SelectedHierarchy nodes={selection} />
            </div>
          </>
        ) : (
          <>
            <h2 className="text-lg font-bold leading-6 text-ink">
              Election Commission Level Hierarchy
            </h2>
            <div className="mt-6">
              <EcHierarchyTree rootLabel="Alwar Assembly Constituency" />
            </div>
          </>
        )}
      </Card>

      {/* Right — editable organization levels */}
      <Card>
        <div className="flex items-start justify-between gap-4">
          <h2 className="text-lg font-bold leading-[22px] text-ink">Organization Level Hierarchy</h2>
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            className="flex shrink-0 items-center gap-2 rounded-lg border-2 border-brand px-3 py-1.5 text-brand transition-colors hover:bg-brand-shade"
          >
            <span className="text-lg font-bold leading-none">+</span>
            <span className="text-left text-[10px] font-bold uppercase leading-[13px]">
              Add New
              <br />
              Level
            </span>
          </button>
        </div>

        <div className="mt-10 flex flex-col gap-4">
          {levels.map((level, i) => (
            <div
              key={level.id}
              className="flex items-center gap-6 rounded-2xl border border-line-soft bg-white p-4"
            >
              <span className="flex size-12 shrink-0 items-center justify-center rounded-full bg-[#eff6ff] text-lg font-bold text-brand">
                {i + 1}
              </span>

              {editingId === level.id ? (
                <Input
                  autoFocus
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onBlur={saveEdit}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveEdit();
                    if (e.key === 'Escape') setEditingId(null);
                  }}
                  className="h-9 flex-1"
                />
              ) : (
                <span className="flex-1 text-base font-bold text-ink">{level.name}</span>
              )}

              <div className="flex items-center gap-3 text-[#d1d5db]">
                <button
                  type="button"
                  aria-label={`Edit ${level.name}`}
                  onClick={() => startEdit(level)}
                  className="transition-colors hover:text-brand"
                >
                  <EditLineIcon className="size-4" />
                </button>
                <button
                  type="button"
                  aria-label={`Delete ${level.name}`}
                  onClick={() => deleteLevel(level.id)}
                  className="transition-colors hover:text-red-500"
                >
                  <TrashIcon className="size-4" />
                </button>
              </div>
            </div>
          ))}

          {levels.length === 0 && (
            <p className="rounded-2xl border border-dashed border-line py-10 text-center text-sm text-ink-secondary">
              No levels yet. Use “Add New Level” to start building the hierarchy.
            </p>
          )}
        </div>
      </Card>

      <AddLevelModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreate={createLevel}
        parents={levels.map((l) => l.name)}
      />
    </div>
  );
}
